import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { RefreshCw, TrendingUp, AlertCircle, Users, Truck } from "lucide-react";

const getColor = (pct) => {
  if (pct >= 80) return "cc-green";
  if (pct >= 50) return "cc-yellow";
  if (pct >= 25) return "cc-orange";
  return "cc-red";
};

const getHoldColor = (count) => {
  if (count === 0) return "cc-green";
  if (count <= 5) return "cc-yellow";
  if (count <= 15) return "cc-orange";
  return "cc-red";
};

const ROWS = [
  { key: "total_dts", label: "Total DTs", format: v => v?.toLocaleString() || "-" },
  { key: "ci_done", label: "CI Done", format: v => v?.toLocaleString() || "-" },
  { key: "dtm_done", label: "DTM Done", format: v => v?.toLocaleString() || "-" },
  { key: "mi_done", label: "MI Done", format: v => v?.toLocaleString() || "-" },
  { key: "qc_passed", label: "QC Passed", format: v => v?.toLocaleString() || "-" },
  { key: "paid", label: "Paid", format: v => v?.toLocaleString() || "-" },
  { key: "saturation_pct", label: "Saturation %", format: v => `${v || 0}%`, colorFn: getColor },
  { key: "on_hold", label: "On Hold", format: v => v?.toLocaleString() || "0", colorFn: getHoldColor },
  { key: "holds_gmr", label: "GMR Holds", format: v => v?.toLocaleString() || "0", colorFn: getHoldColor },
  { key: "holds_discom", label: "DISCOM Holds", format: v => v?.toLocaleString() || "0", colorFn: getHoldColor },
  { key: "holds_inovit", label: "INOVIT Holds", format: v => v?.toLocaleString() || "0", colorFn: getHoldColor },
  { key: "active_teams", label: "Active Teams", format: v => v?.toLocaleString() || "-" },
  { key: "vehicles_active", label: "Vehicles Active", format: v => v?.toLocaleString() || "-" },
  { key: "vehicles_down", label: "Vehicles Down", format: v => v?.toLocaleString() || "0", colorFn: v => v > 0 ? "cc-red" : "cc-green" },
];

export default function CommandCenter() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard/command-center");
      setData(res.data);
      setLastRefresh(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-7 w-48 bg-slate-200 rounded animate-pulse"></div>
        </div>
        <div className="flex items-center justify-center h-64 text-slate-400">
          <RefreshCw size={24} className="animate-spin mr-2" /> Loading Command Center...
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="command-center">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>Command Center</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            All {data.length} circles · {lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString()}` : ""}
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600 hover:border-orange-400 hover:text-orange-600 transition-colors"
          data-testid="cc-refresh-btn"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs flex-wrap">
        {[
          { cls: "cc-green", label: "Good (≥80%)" },
          { cls: "cc-yellow", label: "Caution (50-79%)" },
          { cls: "cc-orange", label: "Warning (25-49%)" },
          { cls: "cc-red", label: "Critical (<25%)" },
        ].map(l => (
          <span key={l.label} className={`px-2 py-1 rounded-sm font-semibold ${l.cls}`}>{l.label}</span>
        ))}
      </div>

      {/* Scrollable Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-auto shadow-sm">
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr className="sticky top-0 bg-slate-900 z-10">
              <th className="cc-row-label bg-slate-900 text-slate-300 text-xs font-bold uppercase tracking-wide py-3 pl-3 pr-6 border-r border-slate-700 z-20 sticky left-0 min-w-[140px]">
                Metric
              </th>
              {data.map(circle => (
                <th
                  key={circle.circle_name}
                  className="text-center text-xs font-bold text-white py-3 px-3 min-w-[100px] border-r border-slate-700 whitespace-nowrap"
                >
                  {circle.circle_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row, ri) => (
              <tr key={row.key} className={ri % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                <td className="cc-row-label font-semibold text-slate-700 text-xs bg-white border-r border-slate-200 sticky left-0">
                  {row.label}
                </td>
                {data.map(circle => {
                  const val = circle[row.key];
                  const colorClass = row.colorFn ? row.colorFn(val) : "";
                  return (
                    <td
                      key={circle.circle_name}
                      className={`cc-cell text-xs font-bold border-r border-slate-100 ${colorClass}`}
                      data-testid={`cc-${circle.circle_name}-${row.key}`}
                    >
                      {row.format(val)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Circle Cards (Mobile-friendly summary) */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {data.map(circle => (
          <div key={circle.circle_name} className="bg-white border border-slate-200 rounded-sm p-3 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{circle.circle_name}</span>
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-sm ${getColor(circle.saturation_pct)}`}>
                {circle.saturation_pct}%
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1 text-[10px]">
              <div className="text-slate-500">MI Done: <span className="font-bold text-orange-600">{circle.mi_done}</span></div>
              <div className="text-slate-500">Total: <span className="font-bold">{circle.total_dts}</span></div>
              <div className="text-slate-500">Holds: <span className={`font-bold ${circle.on_hold > 0 ? "text-red-600" : "text-green-600"}`}>{circle.on_hold}</span></div>
              <div className="text-slate-500">Teams: <span className="font-bold">{circle.active_teams}</span></div>
            </div>
            {/* Mini progress bar */}
            <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${circle.saturation_pct >= 80 ? "bg-green-500" : circle.saturation_pct >= 50 ? "bg-yellow-500" : circle.saturation_pct >= 25 ? "bg-orange-500" : "bg-red-500"}`}
                style={{ width: `${circle.saturation_pct}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
